"use client"

import { motion } from "motion/react"

import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { FADE_UP, FADE_TRANSITION } from "@/lib/motion-presets"

interface ProductsPageHeroProps {
  children: React.ReactNode
}

export function ProductsPageHero({ children }: ProductsPageHeroProps) {
  return (
    <div className="min-h-screen relative">
      {/* Ambient liquid-glass field — a soft brand-green wash + blurred blobs
          that the frosted product cards and feature bands refract against.
          Sits behind all content (-z-10), pinned to the page box. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-linear-to-b from-green-50/40 via-transparent to-emerald-50/30" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[680px] w-[1200px] rounded-full bg-green-200/40 blur-[130px]" />
        <div className="absolute top-[40%] -right-48 h-[520px] w-[520px] rounded-full bg-emerald-200/30 blur-[130px]" />
        <div className="absolute bottom-24 -left-48 h-[500px] w-[500px] rounded-full bg-amber-100/35 blur-[130px]" />
      </div>
      <div className="relative">
        {/* Hero renders immediately - outside Suspense */}
        <motion.div
          {...FADE_UP}
          transition={FADE_TRANSITION}
          className="max-w-6xl mx-auto px-4 pt-6 md:pt-8 md:px-6 lg:px-8"
        >
          <div className="mb-2">
            <BreadcrumbComponent emitSchema={false} items={[{ label: "Home", href: "/" }, { label: "Products" }]} />
          </div>
          <div className="py-2 md:py-3">
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-gray-900 leading-tight">Lawn Care Products</h1>
            <p className="mt-1 text-sm text-gray-600 leading-snug max-w-2xl">
              Expert-curated for all grass types and seasons
              <span className="text-gray-400"> · As an Amazon Associate we earn from qualifying purchases.</span>
            </p>
          </div>
        </motion.div>

        {/* Dynamic content streams in via Suspense */}
        {children}
      </div>
    </div>
  )
}
